import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Typography, CircularProgress, MenuItem, FormControl, InputLabel, Select, Chip,
  Divider, Grid, Tabs, Tab, Avatar, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import ScaleIcon from '@mui/icons-material/Scale';
import {
  productService, pressFormService, machineService, dyeService,
  rawMaterialService, packagingMaterialService,
} from '../services/api';
import api from '../services/api';
import EntityImportExportButtons from '../components/EntityImportExportButtons';
import PageBackHeader from '../components/PageBackHeader';
import NomenclatureUsageDialog, { checkNomenclatureDelete } from '../components/NomenclatureUsageDialog';

const emptyConfigRow = () => ({ press_form: '', machine: '', production_time_seconds: '', number_of_cavities: '' });

function SectionHeader({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
      <Typography variant="subtitle1" fontWeight={700}>{children}</Typography>
    </Box>
  );
}

function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editOpenedRef = useRef(false);
  const [products, setProducts] = useState([]);
  const [pressForms, setPressForms] = useState([]);
  const [machines, setMachines] = useState([]);
  const [dyes, setDyes] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [packagingMaterials, setPackagingMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialogTab, setDialogTab] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imageObjectUrl, setImageObjectUrl] = useState(null);
  const [photoUploadStatus, setPhotoUploadStatus] = useState(null);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageDialogTitle, setMessageDialogTitle] = useState('');
  const [messageDialogText, setMessageDialogText] = useState('');
  const [usageDialog, setUsageDialog] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [customers, setCustomers] = useState([]);
  // Пакування по замовниках
  const [customerPackaging, setCustomerPackaging] = useState([]); // [{id, customer, customer_name, packaging_material, packaging_material_name, quantity}]
  const [productCustomers, setProductCustomers] = useState([]); // замовники у яких є цей виріб
  const [custPkgLoading, setCustPkgLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '', article: '',
    manufacturing_configs: [emptyConfigRow()],
    dyes: [], raw_materials: [],
    raw_material_components: [], dye_components: [],
    product_weight: '', unit_price: '',
    base_price_form1: '', base_price_form2: '',
    packaging_materials: [], packaging_components: [],
    description: '',
  });

  const getProductImageSrc = (product) => {
    const src = product?.image_url || product?.image || null;
    if (!src || typeof src !== 'string') return null;
    if (src.startsWith('http://') || src.startsWith('https://')) return src;
    if (src.startsWith('//')) return `${window.location.protocol}${src}`;
    if (src.startsWith('/')) return `${window.location.origin}${src}`;
    return src;
  };

  useEffect(() => { loadData(); }, []);

  // Deep-link з номенклатури: /products?edit=<id>
  useEffect(() => {
    if (loading || editOpenedRef.current) return;
    const editId = searchParams.get('edit');
    if (!editId) return;
    const product = products.find((p) => String(p.id) === String(editId));
    if (!product) return;
    editOpenedRef.current = true;
    handleOpen(product);
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  }, [loading, products, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const extractErrorMessage = (data, fallback) => {
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.percentage_total) return data.percentage_total;
    if (data.error) return data.error;
    const firstKey = Object.keys(data)[0];
    if (!firstKey) return fallback;
    const v = data[firstKey];
    return typeof v === 'string' ? v : JSON.stringify(v);
  };

  const showMessageDialog = (title, text) => {
    setMessageDialogTitle(title || 'Повідомлення');
    setMessageDialogText(text || '');
    setMessageDialogOpen(true);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsRes, pressFormsRes, machinesRes, dyesRes, rawRes, packRes] = await Promise.all([
        productService.getAll(), pressFormService.getAll(), machineService.getAll(),
        dyeService.getAll(), rawMaterialService.getAll(), packagingMaterialService.getAll(),
      ]);
      const toArray = (d) => Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
      setProducts(toArray(productsRes.data));
      setPressForms(toArray(pressFormsRes.data));
      setMachines(toArray(machinesRes.data));
      setDyes(toArray(dyesRes.data));
      setRawMaterials(toArray(rawRes.data));
      setPackagingMaterials(toArray(packRes.data));
      // Завантажуємо замовників для фільтру
      api.get('/customers/', { params: { page_size: 500 } })
        .then(r => setCustomers(toArray(r.data)))
        .catch(() => {});
    } catch (error) {
      showMessageDialog('Помилка завантаження', error.message || 'Невідома помилка');
    } finally {
      setLoading(false);
    }
  };

  const getCompatibleMachines = (pressFormId) => {
    if (!pressFormId) return [];
    const pfId = Number(pressFormId);
    const compatible = machines.filter((m) => (m.compatible_press_forms || []).includes(pfId));
    // Якщо сумісність не налаштована — показати всі машини
    return compatible.length > 0 ? compatible : machines;
  };

  const getCavitiesForPressForm = (pressFormId) => {
    if (!pressFormId) return '—';
    const pf = pressForms.find((p) => p.id === Number(pressFormId));
    return pf?.number_of_cavities ?? '—';
  };

  const applyProductToForm = (product) => {
    const configs = (product.manufacturing_configs || []).length > 0
      ? product.manufacturing_configs.map((c) => ({
          press_form: c.press_form ?? '', machine: c.machine ?? '',
          production_time_seconds: String(c.production_time_seconds ?? ''),
          number_of_cavities: c.number_of_cavities != null ? String(c.number_of_cavities) : '',
        }))
      : [emptyConfigRow()];
    setFormData({
      name: product.name, article: product.article || '',
      manufacturing_configs: configs,
      dyes: product.dyes || [], raw_materials: product.raw_materials || [],
      raw_material_components: (product.raw_material_components || []).map((c) => ({
        raw_material: c.raw_material?.id ?? c.raw_material,
        percentage: String(c.percentage ?? ''),
        alternatives: c.alternatives || [],
      })),
      dye_components: (product.dye_components || []).map((c) => ({
        dye: c.dye?.id ?? c.dye,
        percentage: String(c.percentage ?? ''),
        alternatives: c.alternatives || [],
      })),
      product_weight: product.product_weight != null ? String(product.product_weight * 1000) : '',
      unit_price: product.unit_price != null ? String(product.unit_price) : '',
      base_price_form1: product.base_price_form1 != null ? String(product.base_price_form1) : '',
      base_price_form2: product.base_price_form2 != null ? String(product.base_price_form2) : '',
      packaging_materials: product.packaging_materials || [],
      packaging_components: (product.packaging_components || []).map((c) => ({
        packaging_material: c.packaging_material ?? '',
        quantity: String(c.quantity ?? 1),
      })),
      description: product.description || '',
    });
  };

  const handleOpen = async (product = null) => {
    if (imageObjectUrl) URL.revokeObjectURL(imageObjectUrl);
    setImageObjectUrl(null);
    setImageFile(null);
    setImagePreviewUrl(getProductImageSrc(product));
    setPhotoUploadStatus(null);
    setDialogTab(0);

    if (product) {
      setEditing(product.id);
      applyProductToForm(product); // швидкий fill зі списку
      setOpen(true);
      // Повний retrieve — список без dyes_data / raw_materials_data / packaging_materials_data
      try {
        const res = await productService.getById(product.id);
        const full = res.data;
        setImagePreviewUrl(getProductImageSrc(full));
        applyProductToForm(full);
      } catch {
        /* залишаємо дані зі списку */
      }
      setCustPkgLoading(true);
      Promise.all([
        api.get('/customer-product-packaging/', { params: { product: product.id, page_size: 500 } }),
        api.get('/customers/', { params: { page_size: 500 } }),
      ]).then(([pkgRes, custRes]) => {
        setCustomerPackaging(pkgRes.data?.results || pkgRes.data || []);
        const allCustomers = custRes.data?.results || custRes.data || [];
        const withProduct = allCustomers.filter(c =>
          (c.product_prices || []).some(p => p.product === product.id || p.product?.id === product.id)
        );
        setProductCustomers(withProduct.map(c => ({ id: c.id, name: c.name })));
      }).catch(() => {}).finally(() => setCustPkgLoading(false));
    } else {
      setEditing(null);
      setCustomerPackaging([]);
      setProductCustomers([]);
      setFormData({
        name: '', article: '',
        manufacturing_configs: [emptyConfigRow()],
        dyes: [], raw_materials: [],
        raw_material_components: [], dye_components: [],
        product_weight: '', unit_price: '',
        base_price_form1: '', base_price_form2: '',
        packaging_materials: [], packaging_components: [],
        description: '',
      });
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false); setEditing(null);
    if (imageObjectUrl) URL.revokeObjectURL(imageObjectUrl);
    setImageObjectUrl(null); setImageFile(null); setImagePreviewUrl(null); setPhotoUploadStatus(null);
  };

  const updateConfig = (index, field, value) => {
    setFormData((prev) => {
      const next = [...prev.manufacturing_configs];
      next[index] = { ...next[index], [field]: value };
      if (field === 'press_form') {
        next[index].machine = '';
        const pf = pressForms.find((p) => p.id === Number(value));
        if (pf) next[index].production_time_seconds = String(pf.production_time_seconds ?? '');
      }
      return { ...prev, manufacturing_configs: next };
    });
  };

  const addConfigRow = () => setFormData((p) => ({ ...p, manufacturing_configs: [...p.manufacturing_configs, emptyConfigRow()] }));
  const removeConfigRow = (i) => setFormData((p) => ({ ...p, manufacturing_configs: p.manufacturing_configs.filter((_, idx) => idx !== i) }));

  const addPackagingComponentRow = () => setFormData((p) => ({ ...p, packaging_components: [...p.packaging_components, { packaging_material: '', quantity: '1' }] }));
  const updatePackagingComponentRow = (i, field, value) => setFormData((p) => { const next = [...p.packaging_components]; next[i] = { ...next[i], [field]: value }; return { ...p, packaging_components: next }; });
  const removePackagingComponentRow = (i) => setFormData((p) => ({ ...p, packaging_components: p.packaging_components.filter((_, idx) => idx !== i) }));

  const addRawComponentRow = () => setFormData((p) => ({ ...p, raw_material_components: [...p.raw_material_components, { raw_material: '', percentage: '', alternatives: [] }] }));
  const updateRawComponentRow = (i, field, value) => setFormData((p) => { const next = [...p.raw_material_components]; next[i] = { ...next[i], [field]: value }; return { ...p, raw_material_components: next }; });
  const removeRawComponentRow = (i) => setFormData((p) => ({ ...p, raw_material_components: p.raw_material_components.filter((_, idx) => idx !== i) }));

  const addAlternativeToRawComponent = (i, altId) => {
    setFormData((p) => {
      const next = [...p.raw_material_components];
      const row = { ...next[i] };
      const rm = rawMaterials.find((r) => r.id === Number(altId));
      if (!rm || (row.alternatives || []).some((a) => a.id === rm.id)) return p;
      row.alternatives = [...(row.alternatives || []), { id: rm.id, name: rm.name, article: rm.article }];
      next[i] = row;
      return { ...p, raw_material_components: next };
    });
  };

  const removeAlternativeFromRawComponent = (i, altId) => {
    setFormData((p) => {
      const next = [...p.raw_material_components];
      next[i] = { ...next[i], alternatives: (next[i].alternatives || []).filter((a) => a.id !== altId) };
      return { ...p, raw_material_components: next };
    });
  };

  const addDyeComponentRow = () => setFormData((p) => ({ ...p, dye_components: [...p.dye_components, { dye: '', percentage: '', alternatives: [] }] }));
  const updateDyeComponentRow = (i, field, value) => setFormData((p) => { const next = [...p.dye_components]; next[i] = { ...next[i], [field]: value }; return { ...p, dye_components: next }; });
  const removeDyeComponentRow = (i) => setFormData((p) => ({ ...p, dye_components: p.dye_components.filter((_, idx) => idx !== i) }));

  const addAlternativeToDyeComponent = (i, altId) => {
    setFormData((p) => {
      const next = [...p.dye_components];
      const row = { ...next[i] };
      const dy = dyes.find((d) => d.id === Number(altId));
      if (!dy || (row.alternatives || []).some((a) => a.id === dy.id)) return p;
      row.alternatives = [...(row.alternatives || []), { id: dy.id, name: dy.name, color_code: dy.color_code }];
      next[i] = row;
      return { ...p, dye_components: next };
    });
  };

  const removeAlternativeFromDyeComponent = (i, altId) => {
    setFormData((p) => {
      const next = [...p.dye_components];
      next[i] = { ...next[i], alternatives: (next[i].alternatives || []).filter((a) => a.id !== altId) };
      return { ...p, dye_components: next };
    });
  };

  const sendableRaw = useMemo(() => formData.raw_material_components.filter((c) => c.raw_material && c.percentage !== '').reduce((s, c) => s + (parseFloat(c.percentage) || 0), 0), [formData.raw_material_components]);
  const sendableDye = useMemo(() => formData.dye_components.filter((c) => c.dye && c.percentage !== '').reduce((s, c) => s + (parseFloat(c.percentage) || 0), 0), [formData.dye_components]);
  const totalSendable = sendableRaw + sendableDye;

  const hasIncompleteRows = formData.raw_material_components.some((c) => (c.raw_material && c.percentage === '') || (!c.raw_material && parseFloat(c.percentage) > 0)) ||
    formData.dye_components.some((c) => (c.dye && c.percentage === '') || (!c.dye && parseFloat(c.percentage) > 0));

  const handleSubmit = async () => {
    try {
      setPhotoUploadStatus(null);
      const configs = formData.manufacturing_configs
        .filter((c) => c.press_form && c.machine)
        .map((c) => ({
          press_form: Number(c.press_form),
          machine: Number(c.machine),
          production_time_seconds: parseFloat(c.production_time_seconds) || 0.1,
          number_of_cavities: c.number_of_cavities !== '' && c.number_of_cavities != null ? parseInt(c.number_of_cavities, 10) : null,
        }));
      const payload = {
        name: formData.name, article: formData.article || null,
        manufacturing_configs: configs,
        dyes: formData.dye_components.filter((c) => c.dye).map((c) => Number(c.dye)),
        raw_materials: formData.raw_material_components.filter((c) => c.raw_material).map((c) => Number(c.raw_material)),
        raw_material_components: formData.raw_material_components.filter((c) => c.raw_material && c.percentage !== '').map((c) => ({ raw_material: Number(c.raw_material), percentage: parseFloat(c.percentage) || 0, alternative_ids: (c.alternatives || []).map((a) => a.id) })),
        raw_material_components_in: formData.raw_material_components.filter((c) => c.raw_material && c.percentage !== '').map((c) => ({ raw_material: Number(c.raw_material), percentage: parseFloat(c.percentage) || 0, alternative_ids: (c.alternatives || []).map((a) => a.id) })),
        dye_components: formData.dye_components.filter((c) => c.dye && c.percentage !== '').map((c) => ({ dye: Number(c.dye), percentage: parseFloat(c.percentage) || 0, alternative_ids: (c.alternatives || []).map((a) => a.id) })),
        dye_components_in: formData.dye_components.filter((c) => c.dye && c.percentage !== '').map((c) => ({ dye: Number(c.dye), percentage: parseFloat(c.percentage) || 0, alternative_ids: (c.alternatives || []).map((a) => a.id) })),
        product_weight: formData.product_weight ? parseFloat(formData.product_weight) / 1000 : null,
        unit_price: formData.unit_price !== '' && formData.unit_price != null ? parseFloat(formData.unit_price) : null,
        base_price_form1: formData.base_price_form1 !== '' ? parseFloat(formData.base_price_form1) : null,
        base_price_form2: formData.base_price_form2 !== '' ? parseFloat(formData.base_price_form2) : null,
        packaging_materials: formData.packaging_materials,
        packaging_components: formData.packaging_components.filter((c) => c.packaging_material).map((c) => ({
          packaging_material: Number(c.packaging_material),
          quantity: Math.max(1, parseInt(c.quantity, 10) || 1),
        })),
        description: formData.description,
      };
      if ((payload.raw_material_components.length > 0 || payload.dye_components.length > 0) && Math.abs(totalSendable - 100) > 0.01) {
        showMessageDialog('Помідомлення', hasIncompleteRows ? `Заповніть усі рядки (зараз ${totalSendable.toFixed(1)}%)` : 'Сума відсотків має дорівнювати 100%');
        return;
      }
      if (editing) {
        const res = await productService.update(editing, payload);
        const savedId = res?.data?.id ?? editing;
        if (imageFile) {
          const uploadRes = await productService.uploadImage(savedId, imageFile);
          const returnedSrc = getProductImageSrc(uploadRes?.data);
          if (returnedSrc) setImagePreviewUrl(returnedSrc);
          setPhotoUploadStatus('Фото завантажено');
        }
      } else {
        const res = await productService.create(payload);
        const savedId = res?.data?.id;
        if (savedId && imageFile) {
          const uploadRes = await productService.uploadImage(savedId, imageFile);
          const returnedSrc = getProductImageSrc(uploadRes?.data);
          if (returnedSrc) setImagePreviewUrl(returnedSrc);
          setPhotoUploadStatus('Фото завантажено');
        }
      }
      handleClose();
      loadData();
    } catch (error) {
      const msg = extractErrorMessage(error.response?.data, error.message || 'Невідома помилка');
      setPhotoUploadStatus(null);
      showMessageDialog('Помилка збереження', msg);
    }
  };

  const handleDelete = async (id) => {
    const item = products.find((x) => x.id === id);
    const itemName = item?.name || '';
    try {
      const check = await checkNomenclatureDelete(productService, id, {
        itemName,
        confirmMessage: 'Ви впевнені, що хочете видалити цей виріб?',
      });
      if (check.blocked) {
        setUsageDialog({ usage: check.usage, itemName: check.itemName || itemName });
        return;
      }
      if (!check.confirmed) return;
      await productService.delete(id);
      loadData();
    } catch (error) {
      const data = error?.response?.data;
      if (error?.response?.status === 409 && data?.in_use) {
        setUsageDialog({ usage: data, itemName: data.name || itemName });
        return;
      }
      const msg = data?.detail || data?.error || error?.message || 'Невідома помилка';
      showMessageDialog('Помилка видалення', msg);
    }
  };

  const filteredProducts = useMemo(() => {
    let list = products;
    const q = searchQuery.trim().toLowerCase();
    if (q) list = list.filter((p) => (p.name || '').toLowerCase().includes(q) || (p.article || '').toLowerCase().includes(q));
    if (customerFilter) {
      const custId = Number(customerFilter);
      const cust = customers.find(c => c.id === custId);
      const priceProductIds = new Set((cust?.product_prices || []).map(pp => pp.product));
      list = list.filter(p => priceProductIds.has(p.id));
    }
    return list;
  }, [products, searchQuery, customerFilter, customers]);

  const fmtPrice = (v) => v != null ? `${Number(v).toFixed(2)} грн` : '—';

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <PageBackHeader sx={{ mb: 0 }} />
          <Typography variant="h4">Вироби</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <EntityImportExportButtons entityKey="products" label="Вироби" />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>+ Додати виріб</Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <TextField size="small" fullWidth placeholder="Пошук по назві або артикулу" value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)} />
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Фільтр по замовнику</InputLabel>
          <Select value={customerFilter} label="Фільтр по замовнику" onChange={(e) => setCustomerFilter(e.target.value)}>
            <MenuItem value="">— Всі вироби —</MenuItem>
            {customers.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '', 'uk')).map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ width: 48 }} />
              <TableCell>Назва</TableCell>
              <TableCell>Артикул</TableCell>
              <TableCell>Прес-форми / Станки</TableCell>
              <TableCell align="right">Вага</TableCell>
              <TableCell align="right">Базова ціна</TableCell>
              <TableCell align="right">Ціна Ф1</TableCell>
              <TableCell align="right">Ціна Ф2</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((product) => {
              const imgSrc = getProductImageSrc(product);
              return (
                <TableRow key={product.id} hover>
                  <TableCell>
                    {imgSrc
                      ? <Box component="img" src={imgSrc} sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 1 }} />
                      : <Avatar sx={{ width: 36, height: 36, bgcolor: '#e0e0e0' }}><ImageIcon sx={{ color: '#bdbdbd', fontSize: 18 }} /></Avatar>}
                  </TableCell>
                  <TableCell><Typography fontWeight={600}>{product.name}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{product.article || '—'}</Typography></TableCell>
                  <TableCell>
                    {(product.manufacturing_configs || []).length === 0 ? '—' : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                        {product.manufacturing_configs.map((c) => (
                          <Typography key={c.id} variant="caption">
                            {c.press_form_identifier || c.press_form_name} / {c.machine_identifier || c.machine_name}
                            {c.production_time_seconds != null ? ` — ${c.production_time_seconds} с` : ''}
                            {c.number_of_cavities != null ? `, ${c.number_of_cavities} гн.` : (c.press_form_total_cavities != null ? `, ${c.press_form_total_cavities} гн. (форма)` : '')}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {product.product_weight != null ? `${Math.round(Number(product.product_weight) * 1000)} г` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right"><Typography variant="body2">{fmtPrice(product.unit_price)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" color={product.base_price_form1 ? 'primary' : 'text.disabled'}>{fmtPrice(product.base_price_form1)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" color={product.base_price_form2 ? 'primary' : 'text.disabled'}>{fmtPrice(product.base_price_form2)}</Typography></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Редагувати"><IconButton size="small" onClick={() => handleOpen(product)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Видалити"><IconButton size="small" onClick={() => handleDelete(product.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredProducts.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center"><Typography color="textSecondary" py={3}>Вироби не знайдено</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ---- Product dialog ---- */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ pb: 0, fontWeight: 700, fontSize: 18 }}>
          {editing ? 'Редагувати виріб' : 'Додати виріб'}
        </DialogTitle>

        {/* Header: photo + name + article + prices */}
        <Box sx={{ px: 3, pt: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Photo */}
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Box sx={{ width: 110, height: 110, border: '2px dashed', borderColor: 'divider', borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#fafafa', cursor: 'pointer' }}
                component="label">
                {imagePreviewUrl
                  ? <Box component="img" src={imagePreviewUrl} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  : <Box sx={{ textAlign: 'center' }}><ImageIcon sx={{ color: '#bdbdbd', fontSize: 32 }} /><Typography variant="caption" color="text.secondary" display="block">Фото</Typography></Box>}
                <input type="file" accept="image/*" hidden onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (imageObjectUrl) URL.revokeObjectURL(imageObjectUrl);
                  const nextUrl = URL.createObjectURL(file);
                  setImageObjectUrl(nextUrl); setImageFile(file); setImagePreviewUrl(nextUrl);
                }} />
              </Box>
              {photoUploadStatus && <Typography variant="caption" color="success.main" display="block" textAlign="center">{photoUploadStatus}</Typography>}
            </Box>

            {/* Name + article + weight */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField label="Назва *" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth required size="small" />
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField label="Артикул" value={formData.article}
                  onChange={(e) => setFormData({ ...formData, article: e.target.value })} size="small" sx={{ flex: 1 }} />
                <TextField label="Вага (г)" type="number" value={formData.product_weight}
                  onChange={(e) => setFormData({ ...formData, product_weight: e.target.value })}
                  size="small" sx={{ width: 120 }}
                  inputProps={{ min: 0.1, step: 1 }}
                  placeholder="напр. 14"
                  helperText="Грами" />
              </Box>
            </Box>
          </Box>

          {/* Prices row */}
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f0f4ff', borderRadius: 2, border: '1px solid #c5cae9' }}>
            <Typography variant="caption" fontWeight={700} color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AttachMoneyIcon fontSize="small" />
              Ціни (синхронізуються з довідником цін)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField label="Базова ціна (загальна)" type="number" value={formData.unit_price}
                onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                size="small" sx={{ flex: 1, minWidth: 160 }}
                inputProps={{ min: 0, step: '0.01' }}
                helperText="Якщо немає ціни в прайс-листі" />
              <TextField label="Ціна по Формі 1" type="number" value={formData.base_price_form1}
                onChange={(e) => setFormData({ ...formData, base_price_form1: e.target.value })}
                size="small" sx={{ flex: 1, minWidth: 130 }} inputProps={{ min: 0, step: '0.01' }} />
              <TextField label="Ціна по Формі 2" type="number" value={formData.base_price_form2}
                onChange={(e) => setFormData({ ...formData, base_price_form2: e.target.value })}
                size="small" sx={{ flex: 1, minWidth: 130 }} inputProps={{ min: 0, step: '0.01' }} />
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Tabs for the rest */}
        <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Виготовлення" />
          <Tab label="Техкарта" />
          <Tab label="Пакування" />
          <Tab label="Опис" />
        </Tabs>

        <DialogContent sx={{ px: 3, py: 2, minHeight: 320 }}>
          {/* Tab 0: Manufacturing configs */}
          {dialogTab === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Один виріб може виготовлятися на кількох прес-формах; кожна прес-форма — на кількох станках.
              </Typography>
              {formData.manufacturing_configs.map((row, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                    <FormControl sx={{ minWidth: 180, flex: 1 }} size="small">
                      <InputLabel>Прес-форма</InputLabel>
                      <Select value={row.press_form || ''} label="Прес-форма"
                        onChange={(e) => updateConfig(index, 'press_form', e.target.value)}>
                        <MenuItem value="">— не обрано —</MenuItem>
                        {pressForms.map((pf) => <MenuItem key={pf.id} value={pf.id}>{pf.identifier} — {pf.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 180, flex: 1 }} size="small">
                      <InputLabel>Станок</InputLabel>
                      <Select value={row.machine || ''} label="Станок" disabled={!row.press_form}
                        onChange={(e) => updateConfig(index, 'machine', e.target.value)}>
                        <MenuItem value="">— не обрано —</MenuItem>
                        {getCompatibleMachines(row.press_form).map((m) => <MenuItem key={m.id} value={m.id}>{m.identifier} — {m.name}</MenuItem>)}
                        {row.press_form && getCompatibleMachines(row.press_form).length === 0 && <MenuItem disabled>Немає станків</MenuItem>}
                      </Select>
                    </FormControl>
                    <TextField label="Час (сек)" type="number" size="small"
                      value={row.production_time_seconds}
                      onChange={(e) => updateConfig(index, 'production_time_seconds', e.target.value)}
                      sx={{ width: 110 }} inputProps={{ min: 0.1, step: 0.1 }} />
                    <TextField
                      label="Гнізда"
                      type="number"
                      size="small"
                      value={row.number_of_cavities}
                      onChange={(e) => updateConfig(index, 'number_of_cavities', e.target.value)}
                      sx={{ width: 90 }}
                      inputProps={{ min: 1, step: 1 }}
                      helperText={row.number_of_cavities === '' && row.press_form ? `форма: ${getCavitiesForPressForm(row.press_form)}` : ''}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 40, pt: 0.5 }}>
                      <IconButton color="error" size="small" onClick={() => removeConfigRow(index)}
                        disabled={formData.manufacturing_configs.length <= 1}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              ))}
              <Button startIcon={<AddIcon />} onClick={addConfigRow} variant="outlined" size="small">
                Додати прес-форму / станок
              </Button>
            </Box>
          )}

          {/* Tab 1: Tech card */}
          {dialogTab === 1 && (
            <Box>
              <SectionHeader>Сировина</SectionHeader>
              {formData.raw_material_components.map((row, index) => (
                <Box key={index} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <FormControl sx={{ flex: 1 }} size="small">
                      <InputLabel>Сировина</InputLabel>
                      <Select value={row.raw_material || ''} label="Сировина"
                        onChange={(e) => updateRawComponentRow(index, 'raw_material', e.target.value)}>
                        <MenuItem value="">— не обрано —</MenuItem>
                        {rawMaterials.map((r) => <MenuItem key={r.id} value={r.id}>{r.name} ({r.type})</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField label="% від ваги" type="number" size="small"
                      value={row.percentage} onChange={(e) => updateRawComponentRow(index, 'percentage', e.target.value)}
                      sx={{ width: 120 }} inputProps={{ min: 0, max: 100, step: 0.1 }} />
                    <IconButton color="error" size="small" onClick={() => removeRawComponentRow(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {/* Alternatives */}
                  <Box sx={{ pl: 1, pt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                    {(row.alternatives || []).length > 0 && (
                      <>
                        <Typography variant="caption" color="text.secondary">Альт.:</Typography>
                        {(row.alternatives || []).map((alt) => (
                          <Chip key={alt.id} label={alt.name} size="small" variant="outlined" color="warning"
                            onDelete={() => removeAlternativeFromRawComponent(index, alt.id)} />
                        ))}
                      </>
                    )}
                    {row.raw_material && (
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select
                          displayEmpty
                          value=""
                          onChange={(e) => { if (e.target.value) addAlternativeToRawComponent(index, Number(e.target.value)); }}
                          renderValue={() => <Typography variant="caption" color="text.secondary">+ Альтернатива</Typography>}
                          sx={{ height: 28, fontSize: 12 }}
                        >
                          {rawMaterials
                            .filter((r) => r.id !== Number(row.raw_material) && !(row.alternatives || []).some((a) => a.id === r.id))
                            .map((r) => <MenuItem key={r.id} value={r.id}>{r.name} ({r.type})</MenuItem>)}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Box>
              ))}
              <Button startIcon={<AddIcon />} onClick={addRawComponentRow} size="small" variant="outlined" sx={{ mb: 2 }}>
                Додати сировину
              </Button>

              <SectionHeader>Барвники</SectionHeader>
              {formData.dye_components.map((row, index) => (
                <Box key={index} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <FormControl sx={{ flex: 1 }} size="small">
                      <InputLabel>Барвник</InputLabel>
                      <Select value={row.dye || ''} label="Барвник"
                        onChange={(e) => updateDyeComponentRow(index, 'dye', e.target.value)}>
                        <MenuItem value="">— не обрано —</MenuItem>
                        {dyes.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}{d.color_code ? ` (${d.color_code})` : ''}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField label="% від ваги" type="number" size="small"
                      value={row.percentage} onChange={(e) => updateDyeComponentRow(index, 'percentage', e.target.value)}
                      sx={{ width: 120 }} inputProps={{ min: 0, max: 100, step: 0.1 }} />
                    <IconButton color="error" size="small" onClick={() => removeDyeComponentRow(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {/* Alternatives */}
                  <Box sx={{ pl: 1, pt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                    {(row.alternatives || []).length > 0 && (
                      <>
                        <Typography variant="caption" color="text.secondary">Альт.:</Typography>
                        {(row.alternatives || []).map((alt) => (
                          <Chip key={alt.id} label={alt.name} size="small" variant="outlined" color="warning"
                            onDelete={() => removeAlternativeFromDyeComponent(index, alt.id)} />
                        ))}
                      </>
                    )}
                    {row.dye && (
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select
                          displayEmpty
                          value=""
                          onChange={(e) => { if (e.target.value) addAlternativeToDyeComponent(index, Number(e.target.value)); }}
                          renderValue={() => <Typography variant="caption" color="text.secondary">+ Альтернатива</Typography>}
                          sx={{ height: 28, fontSize: 12 }}
                        >
                          {dyes
                            .filter((d) => d.id !== Number(row.dye) && !(row.alternatives || []).some((a) => a.id === d.id))
                            .map((d) => <MenuItem key={d.id} value={d.id}>{d.name}{d.color_code ? ` (${d.color_code})` : ''}</MenuItem>)}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Box>
              ))}
              <Button startIcon={<AddIcon />} onClick={addDyeComponentRow} size="small" variant="outlined" sx={{ mb: 1 }}>
                Додати барвник
              </Button>
              <Box sx={{ mt: 1 }}>
                <Chip size="small"
                  label={hasIncompleteRows ? `Сума (заповнених): ${totalSendable.toFixed(1)} % — є незаповнені рядки` : `Сума: ${totalSendable.toFixed(1)} %`}
                  color={totalSendable === 0 ? 'default' : Math.abs(totalSendable - 100) < 0.01 ? 'success' : 'warning'} />
              </Box>
            </Box>
          )}

          {/* Tab 2: Packaging */}
          {dialogTab === 2 && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Пакувальні матеріали, що нормативно списуються при відвантаженні.
              </Typography>
              {formData.packaging_components.map((row, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'center' }}>
                  <FormControl sx={{ flex: 1 }} size="small">
                    <InputLabel>Пакувальний матеріал</InputLabel>
                    <Select value={row.packaging_material || ''} label="Пакувальний матеріал"
                      onChange={(e) => updatePackagingComponentRow(index, 'packaging_material', e.target.value)}>
                      <MenuItem value="">— не обрано —</MenuItem>
                      {packagingMaterials.map((p) => <MenuItem key={p.id} value={p.id}>{p.name} ({p.type})</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField label="Кількість, шт" type="number" size="small"
                    value={row.quantity} onChange={(e) => updatePackagingComponentRow(index, 'quantity', e.target.value)}
                    sx={{ width: 110 }} inputProps={{ min: 1, step: 1 }} />
                  <IconButton color="error" size="small" onClick={() => removePackagingComponentRow(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button startIcon={<AddIcon />} onClick={addPackagingComponentRow} size="small" variant="outlined">
                Додати пакування
              </Button>

              {/* Пакування по замовниках */}
              {editing && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InventoryIcon sx={{ fontSize: 18, color: '#6a1b9a' }} />
                    Індивідуальне пакування по замовниках
                  </Typography>
                  {custPkgLoading ? (
                    <CircularProgress size={20} />
                  ) : productCustomers.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      Немає замовників з цим виробом у прайсі
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {productCustomers.map(cust => {
                        const custRows = customerPackaging.filter(r => r.customer === cust.id);
                        return (
                          <Box key={cust.id} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5 }}>
                            <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>{cust.name}</Typography>
                            {custRows.map(row => (
                              <Box key={row.id} sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'center' }}>
                                <FormControl sx={{ flex: 1 }} size="small">
                                  <InputLabel>Пакування</InputLabel>
                                  <Select value={row.packaging_material} label="Пакування"
                                    onChange={async (e) => {
                                      await api.patch(`/customer-product-packaging/${row.id}/`, { packaging_material: e.target.value });
                                      const res = await api.get('/customer-product-packaging/', { params: { product: editing, page_size: 500 } });
                                      setCustomerPackaging(res.data?.results || res.data || []);
                                    }}>
                                    {packagingMaterials.map(p => <MenuItem key={p.id} value={p.id}>{p.name} ({p.type})</MenuItem>)}
                                  </Select>
                                </FormControl>
                                <TextField label="К-сть, шт" type="number" size="small" sx={{ width: 110 }}
                                  value={row.quantity} inputProps={{ min: 1, step: 1 }}
                                  onChange={async (e) => {
                                    const qty = parseInt(e.target.value, 10);
                                    if (!qty || qty < 1) return;
                                    await api.patch(`/customer-product-packaging/${row.id}/`, { quantity: qty });
                                    setCustomerPackaging(prev => prev.map(r => r.id === row.id ? { ...r, quantity: qty } : r));
                                  }} />
                                <IconButton color="error" size="small" onClick={async () => {
                                  await api.delete(`/customer-product-packaging/${row.id}/`);
                                  setCustomerPackaging(prev => prev.filter(r => r.id !== row.id));
                                }}><DeleteIcon fontSize="small" /></IconButton>
                              </Box>
                            ))}
                            <Button size="small" startIcon={<AddIcon />} onClick={async () => {
                              const firstMat = packagingMaterials[0];
                              if (!firstMat) return;
                              try {
                                const res = await api.post('/customer-product-packaging/', {
                                  customer: cust.id, product: editing,
                                  packaging_material: firstMat.id, quantity: 1,
                                });
                                setCustomerPackaging(prev => [...prev, res.data]);
                              } catch (e) {
                                alert(e?.response?.data?.detail || 'Помилка');
                              }
                            }} variant="outlined" sx={{ mt: 0.5 }}>
                              Додати рядок
                            </Button>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Tab 3: Description */}
          {dialogTab === 3 && (
            <TextField label="Опис" multiline rows={6} value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained">Зберегти</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={messageDialogOpen} onClose={() => setMessageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{messageDialogTitle}</DialogTitle>
        <DialogContent><Typography sx={{ whiteSpace: 'pre-wrap' }}>{messageDialogText}</Typography></DialogContent>
        <DialogActions><Button onClick={() => setMessageDialogOpen(false)} variant="contained" size="small">OK</Button></DialogActions>
      </Dialog>

      <NomenclatureUsageDialog
        open={!!usageDialog}
        onClose={() => setUsageDialog(null)}
        usage={usageDialog?.usage}
        itemName={usageDialog?.itemName}
      />
    </Box>
  );
}

export default ProductsPage;
